// slider z najczęściej kupowanymi produktami - z szybkim dodawaniem do koszyka

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import classnames from 'classnames';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import {
  useGetCartMostBoughtProducts,
  useGetCartMostBoughtProductsMutation,
  usePostProductQuantity,
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement
} from 'api';
import { Counter, Link } from 'components/controls';
import { AddToCartButton } from 'components/containers';
import { IProductListItem, IUnit } from 'api/types';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/pages/Cart/components/MostBoughtProducts/MostBoughtProducts.module.scss';
import productNoImage from 'assets/images/product-no-image.png';

interface IProductCardProps {
  product: IProductListItem;
}

const ProductCard: FC<IProductCardProps> = ({ product }) => {
  const [unitId, setUnitId] = useState<number>(
    product.product_unit_short_info?.default_unit_id || 0
  );
  const [quantity, setQuantity] = useState(1);
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();

  const price = useMemo(
    () => product.product_price_short_info?.prices?.find((p) => p.unit_id === unitId),
    [unitId, product]
  );

  // reset ilości do minimum przy zmianie jednostki
  useEffect(() => {
    const minQty =
      product.product_unit_short_info?.units?.find((u) => u.unit_id === unitId)
        ?.minimal_quantity || 1;
    setQuantity(minQty);
    setManuallyChangedQuantity(undefined);
  }, [unitId]);

  const { mutate: updateQuantity, mutateAsync: updateQuantityAsync, isLoading: isUpdateLoading } =
    usePostProductQuantity(product.id, {
      onSuccess: (data) => setQuantity(data.data.value)
    });

  const { mutate: incrementQuantity, isLoading: isIncrementLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => setQuantity(data.data.value)
    });

  const { mutate: decrementQuantity, isLoading: isDecrementLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => setQuantity(data.data.value)
    });

  const units = product.product_unit_short_info?.units || [];
  const [imgError, setImgError] = useState(false);
  const image = (!imgError && product.product_image_formatted_short_info?.images?.[0]?.min) || productNoImage;
  const isCounterLoading = isUpdateLoading || isIncrementLoading || isDecrementLoading;

  return (
    <div className={styles.productCard}>
      <Link
        to={`/${product.product_basic_short_info?.url_link}`}
        className={styles.productImage}>
        <img src={image} alt={product.product_basic_short_info?.title} onError={() => setImgError(true)} />
      </Link>
      <div className={styles.productInfo}>
        <Link
          to={`/${product.product_basic_short_info?.url_link}`}
          className={styles.productTitle}>
          {product.product_basic_short_info?.title}
        </Link>
        {price && (
          <div className={styles.prices}>
            <span className={styles.net}>
              <strong>{price.price_net_formatted}</strong> {price.currency} netto
            </span>
            <span className={styles.gross}>
              {price.price_gross_formatted} {price.currency} brutto
            </span>
          </div>
        )}
        <div className={styles.unitSelector}>
          {units.length > 1 ? (
            <WiseSelect<IUnit>
              onChange={(unit) => unit?.unit_id && setUnitId(unit.unit_id)}
              value={unitId}
              options={units.map((unit) => ({
                value: unit.unit_id,
                label: `${unit.name}${unit.converter !== 1 ? ` (${unit.converter})` : ''}`,
                item: unit
              }))}
            />
          ) : (
            <span className={styles.unitLabel}>
              <Trans>Jednostka</Trans>: <strong>{units[0]?.name}</strong>
            </span>
          )}
        </div>
        <div className={styles.addToCart}>
          <Counter
            onChange={(value) => updateQuantity({ value, unit_id: unitId })}
            onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
            onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
            value={quantity}
            onChangeValue={(isChanged, newValue) =>
              setManuallyChangedQuantity(isChanged ? newValue : undefined)
            }
            disabled={isCounterLoading}
          />
          <AddToCartButton
            isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
            updateQuantity={async () => {
              const newQuantity = manuallyChangedQuantity
                ? await updateQuantityAsync({ value: manuallyChangedQuantity, unit_id: unitId })
                : undefined;
              return newQuantity?.data.value === manuallyChangedQuantity
                ? manuallyChangedQuantity
                : undefined;
            }}
            quantity={quantity}
            unitId={unitId}
            productId={product.id}
          />
        </div>
      </div>
    </div>
  );
};

const MostBoughtProducts = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: simpleData, isLoading } = useGetCartMostBoughtProducts(
    { page: 1, limit: 999 },
    {
      onSuccess: (data) => {
        if (data.items.length) {
          refetchFullData({
            scopes: 'PriceProductData',
            productIds: data.items
              .map((item) => item.id)
              .toString()
              .replaceAll(',', '|'),
            page: 1,
            limit: 999
          });
        }
      }
    }
  );

  const { data: fullData, mutate: refetchFullData } = useGetCartMostBoughtProductsMutation();

  const products = useMemo(() => {
    return simpleData?.items?.map((simpleProduct) => {
      return fullData
        ? {
            ...simpleProduct,
            ...fullData.items.find((fullProduct) => fullProduct.id === simpleProduct.id)
          }
        : simpleProduct;
    });
  }, [simpleData, fullData]);

  if (isLoading) {
    return null;
  }

  if (!products?.length) {
    return null;
  }

  const total = products.length;

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Cart-components-MostBoughtProducts')}>
      <div className={styles.header}>
        <span className={styles.title}>
          <Trans>Najczęściej kupowane produkty</Trans>
        </span>
        {total > 1 && (
          <div className={styles.nav}>
            <button
              className={styles.navButton}
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((i) => i - 1)}>
              <ChevronLeft />
            </button>
            <span className={styles.counter}>{currentIndex + 1} / {total}</span>
            <button
              className={styles.navButton}
              disabled={currentIndex === total - 1}
              onClick={() => setCurrentIndex((i) => i + 1)}>
              <ChevronRight />
            </button>
          </div>
        )}
      </div>

      {products[currentIndex] && (
        <ProductCard product={products[currentIndex]} />
      )}
    </div>
  );
};

export default MostBoughtProducts;
